/**
 * Recommended Questions Library
 *
 * Fetches dynamic news-based questions from CloudFront distribution (pool-all-{lang}.json).
 * Falls back to hardcoded questions immediately on any fetch failure.
 * Implements 1-hour client-side caching per language to match CloudFront TTL.
 */

import type { LanguageCode } from './language';
import { logger } from './logger';

export interface QuestionItem {
  displaySummary: string;  // Short text shown on card (e.g., "Fed Rate Impact on Tech Stocks")
  fullQuery: string;       // Full query sent to backend
}

/**
 * Fixed questions that are always available (capabilities and data sources)
 *
 * Backward compatibility:
 * - FIXED_QUESTIONS remains an array (English) for existing consumers/tests.
 * - FIXED_QUESTIONS_BY_LANG provides localized variants for i18n fallback.
 */
export const FIXED_QUESTIONS_BY_LANG: Record<LanguageCode, QuestionItem[]> = {
  en: [
    {
      displaySummary: "What can you help me with in financial analysis?",
      fullQuery: "What are your capabilities and how can you help me with financial analysis and trading?"
    },
    {
      displaySummary: "What financial data can you access?",
      fullQuery: "What financial data, market information, and knowledge sources do you have access to?"
    }
  ],
  zh: [
    {
      displaySummary: "你可以在金融分析方面帮我什么？",
      fullQuery: "你有哪些功能，如何帮助我进行金融分析和交易？"
    },
    {
      displaySummary: "你可以访问哪些金融数据？",
      fullQuery: "你可以访问哪些金融数据、市场信息和知识来源？"
    }
  ],
  es: [
    {
      displaySummary: "¿En qué puedes ayudarme con análisis financiero?",
      fullQuery: "¿Cuáles son tus capacidades y cómo puedes ayudarme con análisis financiero y trading?"
    },
    {
      displaySummary: "¿A qué datos financieros puedes acceder?",
      fullQuery: "¿A qué datos financieros, información de mercado y fuentes de conocimiento tienes acceso?"
    }
  ],
  pt: [
    {
      displaySummary: "Com o que você pode me ajudar em análise financeira?",
      fullQuery: "Quais são suas capacidades e como você pode me ajudar com análise financeira e negociação?"
    },
    {
      displaySummary: "Quais dados financeiros você pode acessar?",
      fullQuery: "Quais dados financeiros, informações de mercado e fontes de conhecimento você tem acesso?"
    }
  ],
  fr: [
    {
      displaySummary: "Comment pouvez-vous m'aider en analyse financière?",
      fullQuery: "Quelles sont vos capacités et comment pouvez-vous m'aider avec l'analyse financière et le trading?"
    },
    {
      displaySummary: "Quelles données financières pouvez-vous consulter?",
      fullQuery: "Quelles données financières, informations de marché et sources de connaissances avez-vous accès?"
    }
  ],
  it: [
    {
      displaySummary: "Come puoi aiutarmi con l'analisi finanziaria?",
      fullQuery: "Quali sono le tue capacità e come puoi aiutarmi con l'analisi finanziaria e il trading?"
    },
    {
      displaySummary: "Quali dati finanziari puoi accedere?",
      fullQuery: "Quali dati finanziari, informazioni di mercato e fonti di conoscenza hai accesso?"
    }
  ],
  nl: [
    {
      displaySummary: "Waarmee kun je me helpen bij financiële analyse?",
      fullQuery: "Wat zijn je mogelijkheden en hoe kun je me helpen met financiële analyse en handel?"
    },
    {
      displaySummary: "Welke financiële gegevens kun je raadplegen?",
      fullQuery: "Welke financiële gegevens, marktinformatie en kennisbronnen heb je toegang tot?"
    }
  ],
  pl: [
    {
      displaySummary: "W czym możesz mi pomóc w analizie finansowej?",
      fullQuery: "Jakie są twoje możliwości i jak możesz pomóc mi w analizie finansowej i tradingu?"
    },
    {
      displaySummary: "Do jakich danych finansowych masz dostęp?",
      fullQuery: "Do jakich danych finansowych, informacji rynkowych i źródeł wiedzy masz dostęp?"
    }
  ],
  de: [
    {
      displaySummary: "Womit können Sie mir bei Finanzanalysen helfen?",
      fullQuery: "Was sind Ihre Fähigkeiten und wie können Sie mir bei Finanzanalysen und Trading helfen?"
    },
    {
      displaySummary: "Auf welche Finanzdaten können Sie zugreifen?",
      fullQuery: "Auf welche Finanzdaten, Marktinformationen und Wissensquellen haben Sie Zugriff?"
    }
  ],
  ar: [
    {
      displaySummary: "كيف يمكنك مساعدتي في التحليل المالي؟",
      fullQuery: "ما هي قدراتك وكيف يمكنك مساعدتي في التحليل المالي والتداول?"
    },
    {
      displaySummary: "ما هي البيانات المالية التي يمكنك الوصول إليها؟",
      fullQuery: "ما هي البيانات المالية ومعلومات السوق ومصادر المعرفة التي يمكنك الوصول إليها؟"
    }
  ]
};

/**
 * Curated questions for fallback when CloudFront is unavailable
 *
 * Backward compatibility:
 * - CURATED_QUESTIONS remains an array (English) for existing consumers/tests.
 * - CURATED_QUESTIONS_BY_LANG provides localized variants for i18n fallback.
 */
export const CURATED_QUESTIONS_BY_LANG: Record<LanguageCode, QuestionItem[]> = {
  en: [
    {
      displaySummary: "Analyze current market trends across major indices",
      fullQuery: "Can you analyze current market trends and provide insights on major stock indices?"
    },
    {
      displaySummary: "Help me identify risks in my investment portfolio",
      fullQuery: "How can you help me analyze my investment portfolio and identify potential risks?"
    },
    {
      displaySummary: "Explain technical indicators for trading decisions",
      fullQuery: "What technical indicators can you calculate and how do they help in trading decisions?"
    },
    {
      displaySummary: "Compare stocks to help me choose investments",
      fullQuery: "Can you compare the performance of multiple stocks and help me choose the best investment?"
    },
    {
      displaySummary: "Assess market risk using key metrics",
      fullQuery: "How do you assess market risk and what metrics do you use for risk analysis?"
    },
    {
      displaySummary: "Suggest trading strategies for different conditions",
      fullQuery: "What are some effective trading strategies for different market conditions?"
    },
    {
      displaySummary: "Analyze earnings reports and their stock impact",
      fullQuery: "Can you analyze company earnings reports and their impact on stock prices?"
    },
    {
      displaySummary: "Interpret market news and its trading impact",
      fullQuery: "How do you interpret market news and assess its impact on trading decisions?"
    }
  ],
  zh: [
    {
      displaySummary: "分析主要指数的当前市场趋势",
      fullQuery: "你能分析当前市场趋势并提供主要股票指数的见解吗？"
    },
    {
      displaySummary: "帮我识别投资组合中的风险",
      fullQuery: "你如何帮我分析投资组合并识别潜在风险？"
    },
    {
      displaySummary: "比较股票帮我选择投资",
      fullQuery: "你能比较多只股票的表现并帮我选择最佳投资吗？"
    }
  ],
  es: [
    {
      displaySummary: "Analiza tendencias de mercado en índices principales",
      fullQuery: "¿Puedes analizar las tendencias actuales del mercado y proporcionar información sobre los principales índices bursátiles?"
    },
    {
      displaySummary: "Ayúdame a identificar riesgos en mi cartera",
      fullQuery: "¿Cómo puedes ayudarme a analizar mi cartera de inversión e identificar riesgos potenciales?"
    },
    {
      displaySummary: "Compara acciones para elegir inversiones",
      fullQuery: "¿Puedes comparar el rendimiento de múltiples acciones y ayudarme a elegir la mejor inversión?"
    }
  ],
  pt: [
    {
      displaySummary: "Analise tendências de mercado em índices principais",
      fullQuery: "Você pode analisar as tendências atuais do mercado e fornecer insights sobre os principais índices de ações?"
    },
    {
      displaySummary: "Ajude-me a identificar riscos na minha carteira",
      fullQuery: "Como você pode me ajudar a analisar minha carteira de investimentos e identificar riscos potenciais?"
    },
    {
      displaySummary: "Compare ações para escolher investimentos",
      fullQuery: "Você pode comparar o desempenho de várias ações e me ajudar a escolher o melhor investimento?"
    }
  ],
  fr: [
    {
      displaySummary: "Analysez les tendances du marché sur les principaux indices",
      fullQuery: "Pouvez-vous analyser les tendances actuelles du marché et fournir des informations sur les principaux indices boursiers?"
    },
    {
      displaySummary: "Aidez-moi à identifier les risques de mon portefeuille",
      fullQuery: "Comment pouvez-vous m'aider à analyser mon portefeuille d'investissement et identifier les risques potentiels?"
    },
    {
      displaySummary: "Comparez les actions pour choisir des investissements",
      fullQuery: "Pouvez-vous comparer les performances de plusieurs actions et m'aider à choisir le meilleur investissement?"
    }
  ],
  it: [
    {
      displaySummary: "Analizza le tendenze di mercato sui principali indici",
      fullQuery: "Puoi analizzare le tendenze di mercato attuali e fornire informazioni sui principali indici azionari?"
    },
    {
      displaySummary: "Aiutami a identificare i rischi nel mio portafoglio",
      fullQuery: "Come puoi aiutarmi ad analizzare il mio portafoglio di investimenti e identificare i rischi potenziali?"
    },
    {
      displaySummary: "Confronta le azioni per scegliere gli investimenti",
      fullQuery: "Puoi confrontare le prestazioni di più azioni e aiutarmi a scegliere il miglior investimento?"
    }
  ],
  nl: [
    {
      displaySummary: "Analyseer huidige markttrends over grote indices",
      fullQuery: "Kun je huidige markttrends analyseren en inzichten geven over belangrijke aandelenindices?"
    },
    {
      displaySummary: "Help me risico's in mijn portfolio te identificeren",
      fullQuery: "Hoe kun je me helpen mijn beleggingsportefeuille te analyseren en potentiële risico's te identificeren?"
    },
    {
      displaySummary: "Vergelijk aandelen om investeringen te kiezen",
      fullQuery: "Kun je de prestaties van meerdere aandelen vergelijken en me helpen de beste investering te kiezen?"
    }
  ],
  pl: [
    {
      displaySummary: "Przeanalizuj trendy rynkowe w głównych indeksach",
      fullQuery: "Czy możesz przeanalizować aktualne trendy rynkowe i dostarczyć informacji o głównych indeksach giełdowych?"
    },
    {
      displaySummary: "Pomóż mi zidentyfikować ryzyko w portfelu",
      fullQuery: "Jak możesz pomóc mi przeanalizować mój portfel inwestycyjny i zidentyfikować potencjalne ryzyka?"
    },
    {
      displaySummary: "Porównaj akcje, aby wybrać inwestycje",
      fullQuery: "Czy możesz porównać wyniki wielu akcji i pomóc mi wybrać najlepszą inwestycję?"
    }
  ],
  de: [
    {
      displaySummary: "Analysieren Sie aktuelle Markttrends über große Indizes",
      fullQuery: "Können Sie aktuelle Markttrends analysieren und Einblicke in große Aktienindizes geben?"
    },
    {
      displaySummary: "Helfen Sie mir, Risiken in meinem Portfolio zu identifizieren",
      fullQuery: "Wie können Sie mir helfen, mein Anlageportfolio zu analysieren und potenzielle Risiken zu identifizieren?"
    },
    {
      displaySummary: "Vergleichen Sie Aktien zur Investitionsauswahl",
      fullQuery: "Können Sie die Performance mehrerer Aktien vergleichen und mir helfen, die beste Investition zu wählen?"
    }
  ],
  ar: [
    {
      displaySummary: "تحليل اتجاهات السوق الحالية عبر المؤشرات الرئيسية",
      fullQuery: "هل يمكنك تحليل اتجاهات السوق الحالية وتقديم رؤى حول مؤشرات الأسهم الرئيسية؟"
    },
    {
      displaySummary: "ساعدني في تحديد المخاطر في محفظتي الاستثمارية",
      fullQuery: "كيف يمكنك مساعدتي في تحليل محفظتي الاستثمارية وتحديد المخاطر المحتملة؟"
    },
    {
      displaySummary: "قارن الأسهم لمساعدتي في اختيار الاستثمارات",
      fullQuery: "هل يمكنك مقارنة أداء عدة أسهم ومساعدتي في اختيار أفضل استثمار؟"
    }
  ]
};

// Backward-compatible array exports (English only)
export const FIXED_QUESTIONS: QuestionItem[] = FIXED_QUESTIONS_BY_LANG.en;
export const CURATED_QUESTIONS: QuestionItem[] = CURATED_QUESTIONS_BY_LANG.en;

// DEMO STICKY QUESTION - Always shows first for presentations
// TODO: Remove after demo period
const DEMO_STICKY_QUESTIONS: Record<LanguageCode, QuestionItem> = {
  en: {
    displaySummary: "BTC technical analysis with MACD, RSI, BB",
    fullQuery: "Show me the MACD, RSI and BB of BTC"
  },
  zh: {
    displaySummary: "BTC技术分析：MACD、RSI、BB",
    fullQuery: "显示BTC的MACD、RSI和BB指标"
  },
  es: {
    displaySummary: "Análisis técnico de BTC con MACD, RSI, BB",
    fullQuery: "Muéstrame el MACD, RSI y BB de BTC"
  },
  pt: {
    displaySummary: "Análise técnica de BTC com MACD, RSI, BB",
    fullQuery: "Mostre-me o MACD, RSI e BB de BTC"
  },
  fr: {
    displaySummary: "Analyse technique de BTC avec MACD, RSI, BB",
    fullQuery: "Montrez-moi le MACD, RSI et BB de BTC"
  },
  it: {
    displaySummary: "Analisi tecnica di BTC con MACD, RSI, BB",
    fullQuery: "Mostrami il MACD, RSI e BB di BTC"
  },
  nl: {
    displaySummary: "BTC technische analyse met MACD, RSI, BB",
    fullQuery: "Toon me de MACD, RSI en BB van BTC"
  },
  pl: {
    displaySummary: "Analiza techniczna BTC z MACD, RSI, BB",
    fullQuery: "Pokaż mi MACD, RSI i BB dla BTC"
  },
  de: {
    displaySummary: "BTC technische Analyse mit MACD, RSI, BB",
    fullQuery: "Zeig mir MACD, RSI und BB von BTC"
  },
  ar: {
    displaySummary: "التحليل الفني لـ BTC مع MACD و RSI و BB",
    fullQuery: "أظهر لي MACD و RSI و BB لـ BTC"
  }
};

/**
 * Get demo sticky question for the specified language
 */
function getDemoStickyQuestion(language: LanguageCode): QuestionItem {
  return DEMO_STICKY_QUESTIONS[language] || DEMO_STICKY_QUESTIONS.en;
}

/**
 * CloudFront response interface (matches pool-all.json format)
 */
interface CloudFrontResponse {
  queries: QuestionItem[];
  pool_size: number;
  pool_files_count: number;
  generated_at: string;
}

/**
 * Cache object for storing fetched questions with 1-hour TTL
 */
interface QuestionCache {
  questions: QuestionItem[];
  timestamp: number;
}

// Module-level cache - now per language
const questionCache: Record<LanguageCode, QuestionCache | null> = {
  en: null,
  zh: null,
  es: null,
  pt: null,
  fr: null,
  it: null,
  nl: null,
  pl: null,
  de: null,
  ar: null
};

// Cache TTL: 1 hour (3600000 milliseconds) - matches CloudFront TTL
const CACHE_TTL_MS = 3600000;

// Fetch timeout: 5 seconds (prioritize latency over retries)
const FETCH_TIMEOUT_MS = 5000;

/**
 * Get cached questions if still valid (within 1-hour TTL) for a specific language
 */
function getCachedQuestions(language: LanguageCode): QuestionItem[] | null {
  const cache = questionCache[language];
  if (!cache) {
    return null;
  }

  const now = Date.now();
  const cacheAge = now - cache.timestamp;

  if (cacheAge > CACHE_TTL_MS) {
    // Cache expired
    questionCache[language] = null;
    return null;
  }

  return cache.questions;
}

/**
 * Store questions in cache with current timestamp for a specific language
 */
function setCachedQuestions(questions: QuestionItem[], language: LanguageCode): void {
  questionCache[language] = {
    questions,
    timestamp: Date.now()
  };
}

/**
 * Clear the cache (used by refresh functionality)
 */
export function clearQuestionCache(language?: LanguageCode): void {
  if (language) {
    questionCache[language] = null;
  } else {
    // Clear all language caches
    Object.keys(questionCache).forEach(lang => {
      questionCache[lang as LanguageCode] = null;
    });
  }
}

/**
 * Fetch questions from CloudFront distribution with timeout for a specific language
 * Returns null on any error (immediate fallback, no retries)
 */
async function fetchQueriesFromCloudFront(language: LanguageCode = 'en'): Promise<QuestionItem[] | null> {
  // Dynamically import to avoid circular dependency
  const { getCloudFrontQueriesUrl } = await import('./backend-config');

  let cloudfrontUrl: string;

  try {
    cloudfrontUrl = await getCloudFrontQueriesUrl();
  } catch (error) {
    logger.error('[RecommendedQuestions] Failed to fetch CloudFront URL from config:', error);
    return null;
  }

  // Fallback if CloudFront URL not configured
  if (!cloudfrontUrl || cloudfrontUrl.trim() === '') {
    logger.warn('[RecommendedQuestions] CloudFront URL not configured in backend config, using hardcoded fallback');
    return null;
  }

  // Multi-language queries path (populated by hourly Lambda)
  const v2Url = `${cloudfrontUrl}/queries-v2/pool-all-${language}.json`;
  // Legacy single-language path (pre-i18n)
  const legacyUrl = `${cloudfrontUrl}/queries/pool-all.json`;

  const fetchAndValidate = async (url: string, label: string) => {
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    const response = await fetch(url, {
      signal: controller.signal,
      cache: 'default' // Use browser cache
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      logger.error(`[RecommendedQuestions] CloudFront fetch failed (${label}) for ${language} with status ${response.status}`);
      return null;
    }

    const data: CloudFrontResponse = await response.json();

    // Validate response format
    if (!data.queries || !Array.isArray(data.queries)) {
      logger.error(`[RecommendedQuestions] Invalid CloudFront response format (${label}) for ${language}`);
      return null;
    }

    // Filter out questions missing required fields
    const validQuestions = data.queries.filter(
      q => q.displaySummary && q.fullQuery
    );

    if (validQuestions.length === 0) {
      logger.error(`[RecommendedQuestions] No valid questions in CloudFront response (${label}) for ${language}`);
      return null;
    }

    // Log metadata for monitoring
    logger.info(`[RecommendedQuestions] Fetched ${validQuestions.length} ${language} questions from CloudFront (${label}) (pool_size: ${data.pool_size}, generated_at: ${data.generated_at})`);

    return validQuestions;
  };

  try {
    const v2Questions = await fetchAndValidate(v2Url, 'queries-v2');
    if (v2Questions) {
      return v2Questions;
    }

    const legacyQuestions = await fetchAndValidate(legacyUrl, 'legacy');
    if (legacyQuestions) {
      return legacyQuestions;
    }

    return null;
  } catch (error) {
    // Immediate fallback on any error (no retries)
    if (error instanceof Error && error.name === 'AbortError') {
      logger.error(`[RecommendedQuestions] CloudFront fetch timed out after 5s for ${language}, using hardcoded fallback`);
    } else {
      logger.error(`[RecommendedQuestions] CloudFront fetch failed for ${language}:`, error, ', using hardcoded fallback');
    }
    return null;
  }
}

/**
 * Shuffle array using Fisher-Yates algorithm
 */
export function shuffleArray<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * Randomly select N questions from a pool (no duplicates)
 */
function selectRandomQuestions(pool: QuestionItem[], count: number): QuestionItem[] {
  if (pool.length <= count) {
    // Pool smaller than requested count, return all shuffled
    return shuffleArray(pool);
  }

  // Shuffle and take first N
  const shuffled = shuffleArray(pool);
  return shuffled.slice(0, count);
}

/**
 * Main function: get 4 recommended questions for a specific language
 *
 * Strategy:
 * 1. Check cache first (1-hour TTL per language)
 * 2. If cache expired/empty, fetch from CloudFront (pool-all-{lang}.json)
 * 3. On fetch failure, immediately fallback to FIXED_QUESTIONS_BY_LANG + CURATED_QUESTIONS_BY_LANG
 * 4. Randomly select 4 questions from available pool
 *
 * @param forceRefresh - If true, bypass cache and fetch fresh data
 * @param language - Language code for fetching language-specific questions (default: 'en')
 */
export async function getShuffledRecommendedQuestions(
  forceRefresh = false,
  language: LanguageCode = 'en'
): Promise<QuestionItem[]> {
  let questionPool: QuestionItem[];

  if (!forceRefresh) {
    // Try to use cached questions first for this language
    const cached = getCachedQuestions(language);
    if (cached) {
      // Cache hit - always show demo question first, then 3 random questions
      return [getDemoStickyQuestion(language), ...selectRandomQuestions(cached, 3)];
    }
  }

  // Cache miss or force refresh - fetch from CloudFront for this language
  const cloudFrontQuestions = await fetchQueriesFromCloudFront(language);

  if (cloudFrontQuestions) {
    // Success - cache the full pool for this language and select random 4
    setCachedQuestions(cloudFrontQuestions, language);
    questionPool = cloudFrontQuestions;
  } else {
    // Fallback to hardcoded questions (FIXED + CURATED) in the user's language
    questionPool = [...FIXED_QUESTIONS_BY_LANG[language], ...CURATED_QUESTIONS_BY_LANG[language]];
  }

  // Always show demo question first, then 3 random questions
  return [getDemoStickyQuestion(language), ...selectRandomQuestions(questionPool, 3)];
}
